import { type Locale, type Tr, adminApi } from '@/lib/api';
import { type PromoImage, photoUrl } from '@levamar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

const LOCALES: Locale[] = ['pt', 'en', 'fr', 'es', 'de'];

/**
 * Per-setting typed editors. Each component owns one key, fetches its current value, debounces
 * the form state locally, and writes via PUT /admin/settings/:key on submit.
 *
 * Save signature ergonomics: returns `{ savedAt: number | null, save: () => void, isPending: boolean }`
 * so each form can render a uniform footer.
 */

function useSetting<T>(key: string, defaultValue: T) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'setting', key],
    queryFn: () => adminApi.settingByKey(key),
  });
  const [draft, setDraft] = useState<T>(defaultValue);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data?.value !== undefined && data?.value !== null) {
      setDraft(data.value as T);
    }
  }, [data?.value]);

  useEffect(() => {
    if (savedAt === null) return;
    const t = setTimeout(() => setSavedAt(null), 2400);
    return () => clearTimeout(t);
  }, [savedAt]);

  const mutation = useMutation({
    mutationFn: () => adminApi.putSetting(key, draft),
    onSuccess: () => {
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['admin', 'setting', key] });
    },
  });

  return {
    draft,
    setDraft,
    save: () => mutation.mutate(),
    isPending: mutation.isPending,
    failed: mutation.isError,
    savedAt,
    isLoading,
  };
}

function emptyTr(): Tr {
  return { pt: '', en: '', fr: '', es: '', de: '' };
}

// ───────────────────────── scalar editors ─────────────────────────

interface ScalarValue<T> {
  value: T;
}

export function PhoneSetting() {
  return (
    <ScalarRow
      keyName="support_whatsapp"
      label="WhatsApp do apoio"
      hint="Formato E.164, sem espaços (ex: +238980000)"
      inputType="tel"
    />
  );
}

export function EmailSetting() {
  return (
    <ScalarRow
      keyName="company_email"
      label="Email do apoio"
      hint="Aparece na página /conta/apoio e nos rodapés."
      inputType="email"
    />
  );
}

export function DepositPctSetting() {
  return (
    <ScalarRow
      keyName="deposit_pct"
      label="Sinal a pagar na reserva (%)"
      hint="Percentagem do total cobrada online para confirmar a reserva (1–100). O resto paga-se na entrega. Só afeta reservas novas."
      inputType="number"
      defaultValue={20}
    />
  );
}

export function HoldMinutesSetting() {
  return (
    <ScalarRow
      keyName="hold_minutes"
      label="Tempo de hold pendente (minutos)"
      hint="Quanto tempo uma reserva fica em reserved_pending_payment antes de expirar."
      inputType="number"
      defaultValue={15}
    />
  );
}

export function OverdueGraceSetting() {
  return (
    <ScalarRow
      keyName="overdue_grace_minutes"
      label="Tolerância para overdue (minutos)"
      hint="Quantos minutos após to_at é que a reserva passa a overdue."
      inputType="number"
      defaultValue={60}
    />
  );
}

function ScalarRow({
  keyName,
  label,
  hint,
  inputType,
  defaultValue = '',
}: {
  keyName: string;
  label: string;
  hint: string;
  inputType: 'text' | 'email' | 'tel' | 'number';
  defaultValue?: string | number;
}) {
  const { draft, setDraft, save, isPending, failed, savedAt, isLoading } = useSetting<
    ScalarValue<string | number>
  >(keyName, { value: defaultValue });

  return (
    <SectionShell title={label} keyName={keyName} hint={hint}>
      <input
        type={inputType}
        value={String(draft?.value ?? '')}
        onChange={(e) =>
          setDraft({ value: inputType === 'number' ? Number(e.target.value) : e.target.value })
        }
        style={input}
      />
      <SaveBar onSave={save} isPending={isPending} failed={failed} savedAt={savedAt} isLoading={isLoading} />
    </SectionShell>
  );
}

// ───────────────────────── 5-locale text ─────────────────────────

export function CompanyNameSetting() {
  return (
    <TrTextRow
      keyName="company_name"
      label="Nome da empresa"
      hint="Aparece no header e nos emails. Uma linha por idioma."
      multiline={false}
    />
  );
}

export function CancellationPolicySetting() {
  return (
    <TrTextRow
      keyName="cancellation_policy"
      label="Política de cancelamento"
      hint="Markdown. Aparece em /politica-cancelamento."
      multiline
    />
  );
}

export function TermsSetting() {
  return (
    <TrTextRow keyName="terms" label="Termos e condições" hint="Markdown. Aparece em /termos." multiline />
  );
}

export function PrivacySetting() {
  return (
    <TrTextRow
      keyName="privacy"
      label="Política de privacidade"
      hint="Markdown. Aparece em /privacidade."
      multiline
    />
  );
}

export function CookiesSetting() {
  return (
    <TrTextRow
      keyName="cookies"
      label="Política de cookies"
      hint="Markdown. Aparece em /cookies."
      multiline
    />
  );
}

function TrTextRow({
  keyName,
  label,
  hint,
  multiline,
}: {
  keyName: string;
  label: string;
  hint: string;
  multiline: boolean;
}) {
  const { draft, setDraft, save, isPending, savedAt, isLoading } = useSetting<Tr>(keyName, emptyTr());
  const [activeLocale, setActiveLocale] = useState<Locale>('pt');
  const done = LOCALES.filter((l) => draft[l]?.trim().length > 0).length;

  return (
    <SectionShell title={label} keyName={keyName} hint={hint} completeness={`${done} de 5 idiomas`}>
      <LocaleTabs active={activeLocale} onChange={setActiveLocale} />
      {multiline ? (
        <textarea
          value={draft[activeLocale] ?? ''}
          onChange={(e) => setDraft({ ...draft, [activeLocale]: e.target.value })}
          rows={6}
          style={{ ...input, fontFamily: 'var(--lv-font-mono)' }}
        />
      ) : (
        <input
          type="text"
          value={draft[activeLocale] ?? ''}
          onChange={(e) => setDraft({ ...draft, [activeLocale]: e.target.value })}
          style={input}
        />
      )}
      <SaveBar onSave={save} isPending={isPending} savedAt={savedAt} isLoading={isLoading} />
    </SectionShell>
  );
}

// ───────────────────────── home promo tile (006) ─────────────────────────

interface HomePromoValue {
  enabled: boolean;
  title: Tr;
  body: Tr;
  ctaLabel: Tr;
  ctaHref: string;
  image?: PromoImage | null;
}

const S3_PUBLIC =
  (import.meta.env.VITE_S3_PUBLIC_URL as string | undefined) ?? 'http://localhost:9000/levamar-dev';

export function HomePromoSetting() {
  const { draft, setDraft, save, isPending, failed, savedAt, isLoading } = useSetting<HomePromoValue>(
    'home_promo',
    { enabled: false, title: emptyTr(), body: emptyTr(), ctaLabel: emptyTr(), ctaHref: '' },
  );
  const [activeLocale, setActiveLocale] = useState<Locale>('pt');
  const setTr = (field: 'title' | 'body' | 'ctaLabel', value: string) =>
    setDraft({ ...draft, [field]: { ...draft[field], [activeLocale]: value } });
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { image } = await adminApi.uploadPromoImage(file);
      // Functional update: the admin may have edited other fields while the upload ran.
      setDraft((current) => ({ ...current, image }));
    } catch (err) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <SectionShell
      title="Promoção / aviso na página inicial"
      keyName="home_promo"
      hint="Faixa por baixo dos 2 carros em destaque. O título em PT é obrigatório para ativar; idiomas em branco mostram o texto PT."
    >
      <FieldRow label="Mostrar no site">
        <select
          value={draft.enabled ? 'yes' : 'no'}
          onChange={(e) => setDraft({ ...draft, enabled: e.target.value === 'yes' })}
          style={input}
        >
          <option value="no">Não</option>
          <option value="yes">Sim</option>
        </select>
      </FieldRow>
      {/* Not a FieldRow: that renders a <label>, which must not wrap buttons. */}
      <div style={{ marginTop: 8 }}>
        <span style={fieldLabel}>Imagem (opcional)</span>
        <span style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {draft.image && (
            <img
              src={photoUrl(S3_PUBLIC, draft.image, 640)}
              alt="Pré-visualização da imagem da promoção"
              style={{
                width: 160,
                aspectRatio: '16 / 9',
                objectFit: 'cover',
                border: '1px solid var(--lv-line)',
              }}
            />
          )}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={btnGhost}
          >
            {uploading ? 'A enviar…' : draft.image ? 'Trocar imagem' : '+ Escolher imagem'}
          </button>
          {draft.image && (
            <button
              type="button"
              onClick={() => setDraft((current) => ({ ...current, image: null }))}
              disabled={uploading}
              style={btnDangerSmall}
            >
              Remover
            </button>
          )}
        </span>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onPick}
        style={{ display: 'none' }}
      />
      <p style={{ fontSize: 11, color: uploadError ? 'var(--lv-err)' : 'var(--lv-gray-500)', margin: 0 }}>
        {uploadError ?? 'JPG, PNG ou WebP, até 10 MB, de preferência horizontal. Aplica-se ao guardar.'}
      </p>
      <LocaleTabs active={activeLocale} onChange={setActiveLocale} />
      <FieldRow label={`Título (${activeLocale.toUpperCase()})`}>
        <input
          type="text"
          value={draft.title[activeLocale] ?? ''}
          onChange={(e) => setTr('title', e.target.value)}
          style={input}
        />
      </FieldRow>
      <FieldRow label={`Texto (${activeLocale.toUpperCase()})`}>
        <textarea
          value={draft.body[activeLocale] ?? ''}
          onChange={(e) => setTr('body', e.target.value)}
          rows={3}
          style={input}
        />
      </FieldRow>
      <FieldRow label={`Texto do botão (${activeLocale.toUpperCase()})`}>
        <input
          type="text"
          value={draft.ctaLabel[activeLocale] ?? ''}
          onChange={(e) => setTr('ctaLabel', e.target.value)}
          style={input}
        />
      </FieldRow>
      <FieldRow label="Link do botão (opcional)">
        <input
          type="text"
          placeholder="/frota ou https://…"
          value={draft.ctaHref}
          onChange={(e) => setDraft({ ...draft, ctaHref: e.target.value.trim() })}
          style={input}
        />
      </FieldRow>
      <SaveBar
        onSave={save}
        isPending={isPending}
        failed={failed}
        savedAt={savedAt}
        isLoading={isLoading || uploading}
      />
    </SectionShell>
  );
}

// ───────────────────────── FAQ list editor ─────────────────────────

interface FaqItem {
  q: Tr;
  a: Tr;
}

interface FaqValue {
  items: FaqItem[];
}

export function FaqSetting() {
  const { draft, setDraft, save, isPending, savedAt, isLoading } = useSetting<FaqValue>('faq', { items: [] });
  const [activeLocale, setActiveLocale] = useState<Locale>('pt');

  const items = draft?.items ?? [];

  function setItem(idx: number, next: FaqItem) {
    setDraft({ items: items.map((it, i) => (i === idx ? next : it)) });
  }
  function addItem() {
    setDraft({ items: [...items, { q: emptyTr(), a: emptyTr() }] });
  }
  function removeItem(idx: number) {
    setDraft({ items: items.filter((_, i) => i !== idx) });
  }
  function move(idx: number, delta: number) {
    const next = items.slice();
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    setDraft({ items: next });
  }

  return (
    <SectionShell
      title="FAQ"
      keyName="faq"
      hint="Perguntas e respostas mostradas em /conta/apoio."
      completeness={`${items.length} item(s)`}
    >
      <LocaleTabs active={activeLocale} onChange={setActiveLocale} />

      {items.length === 0 ? (
        <p
          style={{
            fontSize: 12,
            color: 'var(--lv-gray-500)',
            padding: 12,
            border: '1px dashed var(--lv-line)',
            textAlign: 'center',
          }}
        >
          Sem perguntas. Adicione abaixo.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
          {items.map((item, idx) => (
            <li
              key={idx}
              style={{
                border: '1px solid var(--lv-line)',
                padding: 12,
                background: 'var(--lv-surface-soft)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--lv-gray-500)',
                  }}
                >
                  Pergunta #{idx + 1}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} style={btnIcon}>
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === items.length - 1}
                    style={btnIcon}
                  >
                    ↓
                  </button>
                  <button type="button" onClick={() => removeItem(idx)} style={btnDangerSmall}>
                    ✕
                  </button>
                </div>
              </div>
              <FieldRow label={`Pergunta (${activeLocale.toUpperCase()})`}>
                <input
                  type="text"
                  value={item.q[activeLocale] ?? ''}
                  onChange={(e) =>
                    setItem(idx, { ...item, q: { ...item.q, [activeLocale]: e.target.value } })
                  }
                  style={input}
                />
              </FieldRow>
              <FieldRow label={`Resposta (${activeLocale.toUpperCase()})`}>
                <textarea
                  value={item.a[activeLocale] ?? ''}
                  onChange={(e) =>
                    setItem(idx, { ...item, a: { ...item.a, [activeLocale]: e.target.value } })
                  }
                  rows={3}
                  style={input}
                />
              </FieldRow>
            </li>
          ))}
        </ul>
      )}

      <button type="button" onClick={addItem} style={{ ...btnGhost, marginTop: 8 }}>
        + Adicionar pergunta
      </button>
      <SaveBar onSave={save} isPending={isPending} savedAt={savedAt} isLoading={isLoading} />
    </SectionShell>
  );
}

// ───────────────────────── bank accounts ─────────────────────────

interface BankAccount {
  name: string;
  iban: string;
  nib?: string;
  swift?: string;
  currency: 'EUR' | 'CVE';
  isActive: boolean;
}
interface BankAccountsValue {
  items: BankAccount[];
}

function emptyAccount(): BankAccount {
  return { name: '', iban: '', nib: '', swift: '', currency: 'CVE', isActive: true };
}

export function BankAccountsSetting() {
  const { draft, setDraft, save, isPending, savedAt, isLoading } = useSetting<BankAccountsValue>(
    'payment_bank_accounts',
    { items: [] },
  );

  const items = draft?.items ?? [];

  function setItem(idx: number, next: BankAccount) {
    setDraft({ items: items.map((it, i) => (i === idx ? next : it)) });
  }
  function addItem() {
    setDraft({ items: [...items, emptyAccount()] });
  }
  function removeItem(idx: number) {
    setDraft({ items: items.filter((_, i) => i !== idx) });
  }
  function move(idx: number, delta: number) {
    const next = items.slice();
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    setDraft({ items: next });
  }

  const activeCount = items.filter((a) => a.isActive).length;

  return (
    <SectionShell
      title="Contas bancárias para transferência"
      keyName="payment_bank_accounts"
      hint="Mostradas ao cliente quando escolhe transferência bancária. Apenas contas activas aparecem."
      completeness={`${activeCount} activa(s) · ${items.length} total`}
    >
      {items.length === 0 ? (
        <p
          style={{
            fontSize: 12,
            color: 'var(--lv-gray-500)',
            padding: 12,
            border: '1px dashed var(--lv-line)',
            textAlign: 'center',
          }}
        >
          Sem contas. Adicione abaixo.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
          {items.map((item, idx) => (
            <li
              key={idx}
              style={{
                border: '1px solid var(--lv-line)',
                padding: 12,
                background: item.isActive ? 'var(--lv-surface-soft)' : '#faf6f6',
                opacity: item.isActive ? 1 : 0.7,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--lv-gray-500)',
                  }}
                >
                  Conta #{idx + 1}
                  {!item.isActive && ' · inactiva'}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} style={btnIcon}>
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === items.length - 1}
                    style={btnIcon}
                  >
                    ↓
                  </button>
                  <button type="button" onClick={() => removeItem(idx)} style={btnDangerSmall}>
                    ✕
                  </button>
                </div>
              </div>
              <FieldRow label="Nome do banco">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => setItem(idx, { ...item, name: e.target.value })}
                  placeholder="BCA Cabo Verde"
                  style={input}
                />
              </FieldRow>
              <FieldRow label="IBAN">
                <input
                  type="text"
                  value={item.iban}
                  onChange={(e) => setItem(idx, { ...item, iban: e.target.value })}
                  placeholder="CV64 0008 0000 0123 4567 8901 2"
                  style={{ ...input, fontFamily: 'var(--lv-font-mono)' }}
                />
              </FieldRow>
              <FieldRow label="NIB (opcional)">
                <input
                  type="text"
                  value={item.nib ?? ''}
                  onChange={(e) => setItem(idx, { ...item, nib: e.target.value })}
                  placeholder="0008 0000 0123 4567 8901 2"
                  style={{ ...input, fontFamily: 'var(--lv-font-mono)' }}
                />
              </FieldRow>
              <FieldRow label="SWIFT / BIC (opcional)">
                <input
                  type="text"
                  value={item.swift ?? ''}
                  onChange={(e) => setItem(idx, { ...item, swift: e.target.value })}
                  placeholder="BCABCVCV"
                  style={{ ...input, fontFamily: 'var(--lv-font-mono)', textTransform: 'uppercase' }}
                />
              </FieldRow>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                <FieldRow label="Moeda">
                  <select
                    value={item.currency}
                    onChange={(e) => setItem(idx, { ...item, currency: e.target.value as 'EUR' | 'CVE' })}
                    style={input}
                  >
                    <option value="CVE">CVE (Escudo cabo-verdiano)</option>
                    <option value="EUR">EUR (Euro)</option>
                  </select>
                </FieldRow>
                <FieldRow label="Estado">
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 12px',
                      border: '1px solid var(--lv-line)',
                      background: '#fff',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={item.isActive}
                      onChange={(e) => setItem(idx, { ...item, isActive: e.target.checked })}
                    />
                    Activa (visível ao cliente)
                  </label>
                </FieldRow>
              </div>
            </li>
          ))}
        </ul>
      )}

      <button type="button" onClick={addItem} style={{ ...btnGhost, marginTop: 8 }}>
        + Adicionar conta
      </button>
      <SaveBar onSave={save} isPending={isPending} savedAt={savedAt} isLoading={isLoading} />
    </SectionShell>
  );
}

// ───────────────────────── young-driver rule (feature 002 D2) ─────────────────────────

interface YoungDriverRuleValue {
  thresholdAge: number;
  surchargePerDayEur: number;
  surchargePerDayCve: number;
  hardMinAge?: number;
}

/**
 * Young-driver surcharge rule (RD-304) — consumed by the pricing resolver.
 * Set a per-day surcharge for drivers under `thresholdAge`; optionally reject
 * drivers under `hardMinAge`. Leave surcharges at 0 for no charge.
 */
export function YoungDriverRuleSetting() {
  const { draft, setDraft, save, isPending, savedAt, isLoading } = useSetting<YoungDriverRuleValue>(
    'young_driver_rule',
    { thresholdAge: 25, surchargePerDayEur: 0, surchargePerDayCve: 0 },
  );

  const num = (v: number | undefined) => (v == null ? '' : String(v));

  return (
    <SectionShell
      title="Regra de condutor jovem"
      keyName="young_driver_rule"
      hint="Suplemento por dia para condutores abaixo da idade-limite. Idade mínima rígida (opcional) recusa a reserva."
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FieldRow label="Idade-limite (anos)">
          <input
            type="number"
            min={16}
            max={99}
            value={num(draft?.thresholdAge)}
            onChange={(e) => setDraft({ ...draft, thresholdAge: Number(e.target.value) })}
            style={input}
          />
        </FieldRow>
        <FieldRow label="Idade mínima rígida (opcional)">
          <input
            type="number"
            min={16}
            max={99}
            value={num(draft?.hardMinAge)}
            onChange={(e) =>
              setDraft({
                ...draft,
                hardMinAge: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
            style={input}
          />
        </FieldRow>
        <FieldRow label="Suplemento / dia (EUR)">
          <input
            type="number"
            min={0}
            step="0.01"
            value={num(draft?.surchargePerDayEur)}
            onChange={(e) => setDraft({ ...draft, surchargePerDayEur: Number(e.target.value) })}
            style={input}
          />
        </FieldRow>
        <FieldRow label="Suplemento / dia (CVE)">
          <input
            type="number"
            min={0}
            value={num(draft?.surchargePerDayCve)}
            onChange={(e) => setDraft({ ...draft, surchargePerDayCve: Number(e.target.value) })}
            style={input}
          />
        </FieldRow>
      </div>
      <SaveBar onSave={save} isPending={isPending} savedAt={savedAt} isLoading={isLoading} />
    </SectionShell>
  );
}

// ───────────────────────── shared bits ─────────────────────────

function SectionShell({
  title,
  keyName,
  hint,
  completeness,
  children,
}: {
  title: string;
  keyName: string;
  hint: string;
  completeness?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 20 }}>
      <header
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}
      >
        <div>
          {/* Raw setting key stays available on hover for developers, not as permanent UI copy. */}
          <div style={{ fontSize: 13, fontWeight: 700 }} title={keyName}>
            {title}
          </div>
        </div>
        {completeness && <span style={{ fontSize: 11, color: 'var(--lv-gray-500)' }}>{completeness}</span>}
      </header>
      <p style={{ fontSize: 11, color: 'var(--lv-gray-500)', marginBottom: 10 }}>{hint}</p>
      <div style={{ display: 'grid', gap: 8 }}>{children}</div>
    </section>
  );
}

function LocaleTabs({ active, onChange }: { active: Locale; onChange: (l: Locale) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--lv-line)' }}>
      {LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => onChange(l)}
          style={{
            background: active === l ? 'var(--lv-brand)' : 'transparent',
            color: active === l ? '#fff' : '#222',
            border: 0,
            padding: '6px 12px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginTop: 8 }}>
      <span
        style={{
          display: 'block',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--lv-gray-500)',
        }}
      >
        {label}
      </span>
      <span style={{ marginTop: 4, display: 'block' }}>{children}</span>
    </label>
  );
}

function SaveBar({
  onSave,
  isPending,
  savedAt,
  isLoading,
  failed = false,
}: {
  onSave: () => void;
  isPending: boolean;
  failed?: boolean;
  savedAt: number | null;
  isLoading: boolean;
}) {
  return (
    <div
      style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}
    >
      {failed && (
        <span style={{ fontSize: 11, color: 'var(--lv-err)', fontWeight: 700 }}>
          Não guardado — valor inválido
        </span>
      )}
      {savedAt && <span style={{ fontSize: 11, color: 'var(--lv-brand)', fontWeight: 700 }}>✓ Guardado</span>}
      <button type="button" onClick={onSave} disabled={isPending || isLoading} style={btnPrimary}>
        {isPending ? '…' : 'Guardar'}
      </button>
    </div>
  );
}

const input: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--lv-line)',
  background: '#fff',
  fontSize: 13,
};
const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '8px 16px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: '#fff',
  color: '#222',
  border: '1px solid var(--lv-line)',
  padding: '8px 16px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnIcon: React.CSSProperties = {
  background: '#fff',
  color: '#222',
  border: '1px solid var(--lv-line)',
  padding: '4px 8px',
  fontSize: 12,
  cursor: 'pointer',
};
const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--lv-gray-500)',
};
const btnDangerSmall: React.CSSProperties = {
  background: '#fff',
  color: 'var(--lv-err)',
  border: '1px solid var(--lv-err)',
  padding: '4px 8px',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};
